import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { isNil, values as objectValues } from 'lodash';
// Import Types
import type { PushNotificationProvider } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
// Import Services
import { ProviderHttpService } from 'services/http';
// Import UI Components
import { Alert, Button, ExtendedInput, SkeletonLoader } from '@geneui/components';
//Import Components
import ResetSave from 'components/common/ResetSave';
//Import Constants
import { l, AlertTypes, PushNotificationProviderKeys } from 'constants/common';

const { success } = AlertTypes;

interface Props {
    isDefault?: boolean;
    isLoading?: boolean;
    initialData?: PushNotificationProvider['OneSignal'];
    onRefetchData?: () => void;
    onSetDefault?: (providerKey: number) => void;
}

const OneSignalSettingsContent: React.FC<Props> = (props) => {
    const { isDefault, isLoading, initialData, onRefetchData, onSetDefault } = props;
    const { t } = useTranslation();

    const { doPostRequest } = useRequest();
    const { showToaster } = useToaster();

    const isInitialDataEmpty = objectValues(initialData).every((value) => isNil(value));

    const { saveOneSignalSettingRequest } = useCancelableRequests({
        saveOneSignalSettingRequest: ProviderHttpService.setOneSignalSetting,
    });

    const formik = useFormik({
        initialValues: {
            appId: initialData?.AppId ?? '',
            appKey: initialData?.AppKey ?? '',
        },
        validationSchema: Yup.object({
            appId: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.AppId) })),
            appKey: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.AppKey) })),
        }),
        onSubmit: (values) => {
            doPostRequest(saveOneSignalSettingRequest.request, {
                requestBody: { appKey: values.appKey, appId: values.appId },
                successCallback: () => {
                    onRefetchData?.();
                    showToaster(success, t(l.OneSignalSettingsSavedSuccessfully));
                },
            });
        },
    });

    const { values, errors, dirty, isSubmitting, isValid, setFieldValue, submitForm, resetForm } = formik;

    useEffect(() => {
        if (initialData) {
            resetForm({
                values: {
                    appId: initialData.AppId ?? '',
                    appKey: initialData.AppKey ?? '',
                },
            });
        }
    }, [initialData, resetForm]);

    const oneSignalAppIdInputChangeHandler = (e) => {
        setFieldValue('appId', e.target.value);
    };

    const oneSignalAppKeyInputChangeHandler = (e) => {
        setFieldValue('appKey', e.target.value);
    };

    const onSetDefaultHandler = () => {
        onSetDefault?.(PushNotificationProviderKeys.OneSignal);
    };

    return (
        <>
            <Alert closable={false} title={t(l.OneSignalProviderDescription)} iconProps={{ type: 'bc-icon-info' }} />
            <div className="fields-btn-group">
                <div className="input-container">
                    <SkeletonLoader isBusy={isLoading}>
                        <ExtendedInput
                            value={values.appKey}
                            onChange={oneSignalAppKeyInputChangeHandler}
                            disabled={isSubmitting}
                            placeholder={t(l.AppKey)}
                            isValid={!errors.appKey}
                            errorText={errors.appKey}
                            type="password"
                        />
                    </SkeletonLoader>
                </div>
                <div className="input-container">
                    <SkeletonLoader isBusy={isLoading}>
                        <ExtendedInput
                            value={values.appId}
                            onChange={oneSignalAppIdInputChangeHandler}
                            disabled={isSubmitting}
                            placeholder={t(l.AppId)}
                            isValid={!errors.appId}
                            errorText={errors.appId}
                        />
                    </SkeletonLoader>
                </div>
                <div className="push-notification-settings-content-action-buttons-container">
                    <Button
                        appearance="outline"
                        onClick={onSetDefaultHandler}
                        disabled={isDefault || isInitialDataEmpty}
                    >
                        {t(l.SetDefault)}
                    </Button>

                    <ResetSave
                        hasAdditionalSettings={false}
                        resetButtonClickHandler={resetForm}
                        disableSave={!isValid || !dirty || isSubmitting}
                        disableReset={!dirty || isSubmitting}
                        saveButtonClickHandler={submitForm}
                        isLoadingSave={isSubmitting}
                    />
                </div>
            </div>
        </>
    );
};

export default memo(OneSignalSettingsContent);
